// components/invoices/InvoiceBulkOperations.tsx
import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  Box,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Archive as ArchiveIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import axiosInstance from '../../utils/axiosConfig';

interface BulkOperationsProps {
  onRefresh: () => void;
}

interface BulkArchiveResult {
  archivedCount: number;
  criteria: string;
}

const InvoiceBulkOperations: React.FC<BulkOperationsProps> = ({ onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [operation, setOperation] = useState<'archive' | null>(null);
  const [archiveDays, setArchiveDays] = useState(365);
  const [result, setResult] = useState<BulkArchiveResult | null>(null);

  const handleBulkArchiveOld = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post('/api/invoices/archive-old', {
        daysOld: archiveDays
      });
      
      setResult(response.data);
      setConfirmDialogOpen(false);
      onRefresh();
      
    } catch (error: any) {
      console.error('Error bulk archiving:', error);
      alert(error.response?.data?.message || 'Failed to archive old invoices');
    } finally {
      setLoading(false);
    }
  };

  const openConfirmDialog = (op: 'archive') => {
    setOperation(op);
    setConfirmDialogOpen(true);
  };

  return (
    <>
      <Accordion sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="bulk-operations-content"
          id="bulk-operations-header"
          sx={{ 
            minHeight: 48,
            '&.Mui-expanded': { minHeight: 48 },
            '& .MuiAccordionSummary-content': { margin: '8px 0' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AdminIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" color="text.secondary">
              Admin: Bulk Operations
            </Typography>
            <Chip 
              label="Admin Only" 
              size="small" 
              color="warning" 
              variant="outlined"
              sx={{ ml: 1, height: 20, fontSize: '0.65rem' }}
            />
          </Box>
        </AccordionSummary>
        
        <AccordionDetails sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Perform bulk operations on invoices. Use with caution.
          </Typography>

          {/* Compact Archive Section */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            flexWrap: 'wrap',
            p: 2,
            bgcolor: 'background.default',
            borderRadius: 1,
            mb: 2
          }}>
            <Typography variant="body2" sx={{ minWidth: 'fit-content' }}>
              Archive paid/rejected invoices older than:
            </Typography>
            <TextField
              label="Days"
              type="number"
              value={archiveDays}
              onChange={(e) => setArchiveDays(Number(e.target.value))}
              size="small"
              sx={{ width: 80 }}
              inputProps={{ min: 1, max: 9999 }}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<ArchiveIcon />}
              onClick={() => openConfirmDialog('archive')}
              disabled={loading}
            >
              Archive Old
            </Button>
          </Box>

          {/* Results Display */}
          {result && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2">
                ✅ Successfully archived {result.archivedCount} invoices
              </Typography>
            </Alert>
          )}

          {/* Compact Warning */}
          <Alert severity="warning" sx={{ py: 1 }}>
            <Typography variant="caption">
              <strong>⚠️ Warning:</strong> Bulk operations affect multiple invoices. Review criteria carefully.
            </Typography>
          </Alert>
        </AccordionDetails>
      </Accordion>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Confirm Bulk Archive
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Archive Old Invoices</strong>
            </Typography>
            <Typography variant="caption">
              This will archive invoices that are paid/rejected and older than {archiveDays} days.
            </Typography>
          </Alert>

          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary="Status: Paid or Rejected only"
                primaryTypographyProps={{ variant: 'body2' }}
                secondary="Draft/submitted invoices will not be affected"
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <ScheduleIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary={`Age: Older than ${archiveDays} days`}
                primaryTypographyProps={{ variant: 'body2' }}
                secondary={`Before ${new Date(Date.now() - archiveDays * 24 * 60 * 60 * 1000).toLocaleDateString()}`}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="caption">
              Archived invoices can be restored later if needed.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleBulkArchiveOld}
            variant="contained" 
            color="warning"
            startIcon={loading ? <CircularProgress size={16} /> : <ArchiveIcon />}
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Confirm Archive'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InvoiceBulkOperations;